import { normalizeTextProvider } from "../constants/globalAiProviders";
import {
  DREAMFLY_LLM_MODEL_STORAGE_KEY,
  DREAMFLY_LLM_PROVIDER_ID,
  isDreamflyImageApiModel,
} from "../constants/dreamflyModels";

// 获取当前用户ID。兼容旧登录缓存，避免只缺 app_user_id 时把任务记到系统账号。
const getUserId = () => {
  const cachedId = localStorage.getItem("app_user_id");
  if (cachedId) return cachedId;
  try {
    const user = JSON.parse(localStorage.getItem("app_user") || "null");
    return user?.name || user?.username || "default_user";
  } catch {
    return "default_user";
  }
};
const getToken = () => localStorage.getItem("app_token") || "";
const getAppLanguage = () => localStorage.getItem("app_language") || "zh-CN";

const POINT_REFRESH_EVENT = "user-points-refresh";
const TERMINAL_GENERATION_STATUSES = new Set([
  "success",
  "succeeded",
  "completed",
  "complete",
  "done",
  "finished",
  "generated",
  "video_success",
  "image_success",
  "audio_success",
]);
let pointRefreshTimer = null;

const readResponseStatus = (data) => {
  if (!data || typeof data !== "object") return "";
  const candidates = [
    data.status,
    data.taskStatus,
    data.task_status,
    data.currentStage,
    data.current_stage,
    data.result?.status,
    data.task?.status,
  ];
  return String(candidates.find((value) => value != null) || "").trim().toLowerCase();
};

const schedulePointBalanceRefresh = (delay = 2500) => {
  if (typeof window === "undefined") return;
  if (pointRefreshTimer) window.clearTimeout(pointRefreshTimer);
  pointRefreshTimer = window.setTimeout(() => {
    pointRefreshTimer = null;
    window.dispatchEvent(new Event(POINT_REFRESH_EVENT));
  }, delay);
};

const refreshPointsAfterOperation = (url, options, data) => {
  const requestUrl = String(url || "").toLowerCase();
  if (requestUrl.includes("/api/points/me")) return;
  if (requestUrl.includes("/api/admin/generation-maintenance")) return;

  const method = String(options?.method || "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    // 普通保存、编辑、上传都不会改变积分，禁止每个 POST 都抢占积分查询连接。
    if (requestUrl.includes("/api/points/") || requestUrl.includes("/api/admin/points/")) {
      schedulePointBalanceRefresh(200);
    } else if (/(?:generate|generation|generations)(?:[-_/]|\?|$)/.test(requestUrl)) {
      // 扣分由耐久消费者异步完成，稍后刷新，避免在结算刚入队时读到旧值。
      schedulePointBalanceRefresh(2500);
    }
    return;
  }

  const isTaskQuery = /\/(task|tasks|generation|generations|status|result)(\/|\?|$)/.test(requestUrl);
  if (isTaskQuery && TERMINAL_GENERATION_STATUSES.has(readResponseStatus(data))) {
    schedulePointBalanceRefresh(2500);
  }
};

/**
 * 解析响应数据，适配新的 ApiResult 格式
 */
const parseResponse = async (response) => {
  const text = await response.text();
  
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    // 如果不是 JSON，直接返回文本内容
    return text;
  }

  // 如果是统一格式 { code, message, data, timestamp }
  if (data && typeof data.code === 'number') {
    if (data.code === 200) {
      return data.data;
    } else {
      // 关键：业务错误必须抛出，以便被调用方的 catch 捕获
      const error = new Error(data.message || '请求失败');
      error.code = data.code;
      error.data = data;
      throw error;
    }
  }
  
  return data;
};

// Some synchronous generation/storage endpoints legitimately need more than one minute.
// Polling call sites now cancel stale requests themselves, so keep the historical global
// timeout instead of shortening every production request just to protect admin polling.
const DEFAULT_REQUEST_TIMEOUT_MS = 600_000;

const createAbortError = () => {
  if (typeof DOMException !== 'undefined') {
    return new DOMException('The operation was aborted', 'AbortError');
  }
  const error = new Error('The operation was aborted');
  error.name = 'AbortError';
  return error;
};

const abortErrorFromSignal = (signal) => (
  signal?.reason instanceof Error ? signal.reason : createAbortError()
);

const waitForRetry = (delayMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(abortErrorFromSignal(signal));
    return;
  }
  const onAbort = () => {
    clearTimeout(timerId);
    reject(abortErrorFromSignal(signal));
  };
  const timerId = setTimeout(() => {
    signal?.removeEventListener?.('abort', onAbort);
    resolve();
  }, delayMs);
  signal?.addEventListener?.('abort', onAbort, { once: true });
});

export const authenticatedFetch = async (url, options = {}) => {
  const {
    signal: callerSignal,
    timeout: requestedTimeout,
    ...fetchOptions
  } = options;
  // 调用方可为单次图片/文字任务显式覆盖 provider；其余请求沿用全局文字 provider。
  const optionHeaderEntries = typeof Headers !== 'undefined' && options.headers instanceof Headers
    ? Array.from(options.headers.entries())
    : Object.entries(options.headers || {});
  const readOptionHeader = (name) => optionHeaderEntries.find(
    ([key]) => key.toLowerCase() === name.toLowerCase(),
  )?.[1];
  const explicitProvider = String(readOptionHeader('X-Ai-Provider') || '').trim();
  const provider = explicitProvider || normalizeTextProvider(localStorage.getItem("ai_provider"));
  const explicitModel = String(readOptionHeader('X-Ai-Model') || '').trim();
  const dreamflyLlmModel = String(provider).toLowerCase() === DREAMFLY_LLM_PROVIDER_ID
    ? String(localStorage.getItem(DREAMFLY_LLM_MODEL_STORAGE_KEY) || "").trim()
    : "";
  const requestModel = explicitModel || dreamflyLlmModel;
  const forwardedOptionHeaders = Object.fromEntries(optionHeaderEntries.filter(
    ([key]) => !['x-ai-provider', 'x-ai-model'].includes(key.toLowerCase()),
  ));
  const headers = {
    ...forwardedOptionHeaders,
    'X-User-ID': encodeURIComponent(getUserId()),
    'X-Ai-Provider': provider,
    ...(requestModel && (
      String(provider).toLowerCase() !== DREAMFLY_LLM_PROVIDER_ID
      || !isDreamflyImageApiModel(requestModel)
    )
      ? { 'X-Ai-Model': requestModel }
      : {}),
    'X-App-Language': getAppLanguage(),
  };
  const token = getToken();
  const hasHeader = (name) => Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());
  if (token && !hasHeader('Authorization')) {
    headers.Authorization = `Bearer ${token}`;
  }

  // 如果没有 Content-Type 且不是 FormData (上传文件)，默认 JSON
  if (!hasHeader('Content-Type') && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // 设置超时（默认 10 分钟；调用方可按接口显式缩短）
  const parsedTimeout = Number(requestedTimeout);
  const timeout = Number.isFinite(parsedTimeout) && parsedTimeout > 0
    ? parsedTimeout
    : DEFAULT_REQUEST_TIMEOUT_MS;
  const controller = new AbortController();
  let abortSource = null;
  const abortFromCaller = () => {
    if (controller.signal.aborted) return;
    abortSource = 'caller';
    controller.abort(callerSignal?.reason);
  };
  if (callerSignal?.aborted) {
    abortFromCaller();
  } else {
    callerSignal?.addEventListener?.('abort', abortFromCaller, { once: true });
  }
  const timeoutId = setTimeout(() => {
    if (controller.signal.aborted) return;
    abortSource = 'timeout';
    controller.abort();
  }, timeout);

  try {
    const response = await fetch(url, { 
      ...fetchOptions,
      headers,
      signal: controller.signal 
    });
    
    // 如果响应不成功，抛出错误
    if (!response.ok) {
      const errorText = await response.text();
      let errorPayload = null;
      try {
        errorPayload = JSON.parse(errorText);
      } catch {
        // Nginx、代理或旧接口可能返回纯文本/HTML，保留原始错误即可。
      }
      const errorMessage = errorPayload?.message || errorPayload?.error || errorText || `HTTP ${response.status}`;
      const error = new Error(errorMessage);
      // 保留 HTTP 状态，让生成服务区分“明确 4xx 拒绝”和“5xx/网络结果未知”。
      error.status = response.status;
      error.code = errorPayload?.code ?? response.status;
      error.errorCode = errorPayload?.data?.errorCode || errorPayload?.errorCode || null;
      error.data = errorPayload;
      throw error;
    }
    
    // 自动解析并处理新的 ApiResult 格式
    const data = await parseResponse(response);
    refreshPointsAfterOperation(url, fetchOptions, data);
    return data;
  } catch (error) {
    if (abortSource === 'timeout') {
      throw new Error('请求超时，请检查网络连接或稍后重试');
    }
    if (abortSource === 'caller' || callerSignal?.aborted) {
      if (error?.name === 'AbortError') throw error;
      if (callerSignal.reason instanceof Error) throw callerSignal.reason;
      throw createAbortError();
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    callerSignal?.removeEventListener?.('abort', abortFromCaller);
  }
};

/**
 * 带自动重试的 authenticatedFetch
 */
export const authenticatedFetchWithRetry = async (url, options = {}, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    if (options.signal?.aborted) throw abortErrorFromSignal(options.signal);
    try {
      return await authenticatedFetch(url, options);
    } catch (error) {
      if (options.signal?.aborted || error?.name === 'AbortError') throw error;
      if (
        error?.errorCode === 'GENERATION_MAINTENANCE'
        || (error?.status === 503 && error?.message === '当前生成功能正在更新中...')
      ) throw error;
      if (i === retries - 1) throw error;
      // 等待后重试
      await waitForRetry(1000 * (i + 1), options.signal);
    }
  }
};
